import { useEffect, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from "react";
import { GROUPS, PEOPLE, effectiveAccess, type Role, type World } from "../sendItEngine";
import { FILE_META, ROLE_LABEL, ROLE_TEXT, TEAM_ID, fileOf, personOf, placeLabel, placeOf, type FileId } from "./content";
import { Avatar, FileGlyph, Ic, type IcName } from "./icons";

export interface ShareDraft {
  mode: "people" | "link";
  people: string[];
  groups: string[];
  role: Role;
  until: "never" | "done";
  guest: boolean;
}

const ROLE_ICON: Record<Role, IcName> = { view: "eye", comment: "comment", edit: "pencil" };
const ROLES: Role[] = ["view", "comment", "edit"];

/** The Windows-style share dialog. Opened from a file; never a full-page form. */
export function ShareDialog({
  fileId,
  world,
  onCancel,
  onCommit,
  onManage,
  onRequestLocation,
  highlightEmail,
}: {
  fileId: FileId;
  world: World;
  onCancel: () => void;
  onCommit: (draft: ShareDraft) => void;
  onManage: () => void;
  onRequestLocation?: () => void;
  highlightEmail?: string;
}) {
  const [mode, setMode] = useState<"people" | "link">("people");
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [role, setRole] = useState<Role>("view");
  const [until, setUntil] = useState<"never" | "done">("never");
  const [guest, setGuest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(0);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    },
    [],
  );

  const file = fileOf(fileId);
  const meta = FILE_META[fileId];
  const already = effectiveAccess(world, fileId).length;
  const needle = q.trim().toLowerCase();
  const shownPeople = PEOPLE.filter((p) => !needle || `${p.name} ${p.email} ${p.orgLabel}`.toLowerCase().includes(needle));
  const shownGroups = GROUPS.filter(
    (g) =>
      !needle ||
      g.name.toLowerCase().includes(needle) ||
      g.memberIds.some((id) => (personOf(id)?.name ?? "").toLowerCase().includes(needle)),
  );
  const externals = people.map((id) => personOf(id)).filter((p) => p && p.org !== "northstar");
  const ready = mode === "link" || people.length + groups.length > 0;

  const toggle = (set: Dispatch<SetStateAction<string[]>>, id: string) =>
    set((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const go = () => {
    if (busy) return;
    if (!ready) {
      setNudge((n) => n + 1);
      return;
    }
    setBusy(true);
    const draft: ShareDraft = {
      mode,
      people: mode === "people" ? people : [],
      groups: mode === "people" ? groups : [],
      role,
      until: mode === "people" ? until : "never",
      guest: mode === "people" && externals.length > 0 && guest,
    };
    timer.current = window.setTimeout(() => onCommit(draft), 700);
  };

  return (
    <div className={`sis-sh${busy ? " is-busy" : ""}`}>
      <header className="sis-sh-head">
        <FileGlyph ext={meta.ext} size={34} />
        <div className="sis-sh-ht">
          <strong>{file.name}</strong>
          <span>
            {placeLabel(world, fileId)} · {already === 0 ? "Not shared yet" : `Already open to ${already}`}
          </span>
        </div>
        <button type="button" className="sis-link" onClick={onManage}>
          <Ic n="lock" size={13} /> Who has access now
        </button>
      </header>

      {onRequestLocation && placeOf(world, fileId) === "shared-folder" && (
        <div className="sis-move">
          <p>This file is in the shared folder, so people on that folder can still open it.</p>
          <button type="button" className="sis-btn sis-btn--primary hot" onClick={onRequestLocation}>
            Request a restricted project location
          </button>
        </div>
      )}

      <div className="sis-sh-body">
        <section className="sis-sh-who" aria-label="Who">
          <div className="sis-seg" role="tablist" aria-label="Share with">
            <button type="button" role="tab" aria-selected={mode === "people"} className={mode === "people" ? "is-on" : ""} onClick={() => setMode("people")}>
              <Ic n="users" size={14} /> People and groups
            </button>
            <button type="button" role="tab" aria-selected={mode === "link"} className={mode === "link" ? "is-on" : ""} onClick={() => setMode("link")}>
              <Ic n="link" size={14} /> Anyone with the link
            </button>
          </div>

          {mode === "people" ? (
            <>
              <label className="sis-search">
                <Ic n="search" size={14} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search the directory by name or email"
                  aria-label="Search the directory"
                />
              </label>
              <div className="sis-dir sis-scroll" role="listbox" aria-multiselectable="true" aria-label="Directory">
                {shownPeople.length > 0 && <div className="sis-dir-h">People</div>}
                {shownPeople.map((p, i) => {
                  const on = people.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`sis-card${on ? " is-on" : ""}${highlightEmail === p.email ? " hot" : ""}`}
                      style={{ "--i": i } as CSSProperties}
                      onClick={() => toggle(setPeople, p.id)}
                    >
                      <Avatar name={p.name} org={p.org} size={34} />
                      <span className="sis-card-tx">
                        <span className="sis-card-top">
                          <strong>{p.name}</strong>
                          <em>{p.orgLabel}</em>
                        </span>
                        <span className="sis-card-mail">{p.email}</span>
                      </span>
                      <span className="sis-tick" aria-hidden="true"><Ic n="check" size={12} /></span>
                    </button>
                  );
                })}
                {shownGroups.length > 0 && <div className="sis-dir-h">Groups</div>}
                {shownGroups.map((g, i) => {
                  const on = groups.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`sis-card${on ? " is-on" : ""}`}
                      style={{ "--i": shownPeople.length + i } as CSSProperties}
                      onClick={() => toggle(setGroups, g.id)}
                    >
                      <span className="sis-stack" aria-hidden="true">
                        {g.memberIds.map((id) => {
                          const m = personOf(id);
                          return m ? <Avatar key={id} name={m.name} org={m.org} size={28} /> : null;
                        })}
                      </span>
                      <span className="sis-card-tx">
                        <span className="sis-card-top">
                          <strong>{g.name}</strong>
                          <em>Northstar (your organisation)</em>
                        </span>
                        <span className="sis-card-mail">{g.memberIds.length} people · group</span>
                      </span>
                      <span className="sis-tick" aria-hidden="true"><Ic n="check" size={12} /></span>
                    </button>
                  );
                })}
                {shownPeople.length + shownGroups.length === 0 && <div className="sis-none">No one in the directory matches “{q}”.</div>}
              </div>
            </>
          ) : (
            <div className="sis-linkcard">
              <span className="sis-linkcard-ic"><Ic n="globe" size={26} /></span>
              <strong>Anyone with the link</strong>
              <span>Anyone who has the link can open this file. They do not need to sign in, and you cannot see who they are.</span>
            </div>
          )}
        </section>

        <section className="sis-sh-what sis-scroll" aria-label="What they can do">
          <h4>What can they do?</h4>
          <div className="sis-roles" role="radiogroup" aria-label="Permission">
            {ROLES.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={role === r}
                className={`sis-role${role === r ? " is-on" : ""}`}
                onClick={() => setRole(r)}
              >
                <span className="sis-radio" aria-hidden="true" />
                <span className="sis-role-ic"><Ic n={ROLE_ICON[r]} size={15} /></span>
                <span className="sis-role-tx">
                  <strong>{ROLE_LABEL[r]}</strong>
                  <span>{ROLE_TEXT[r]}</span>
                </span>
              </button>
            ))}
          </div>

          {mode === "people" && (
            <>
              <h4>Access until</h4>
              <div className="sis-seg sis-seg--sm" role="radiogroup" aria-label="Access until">
                <button type="button" role="radio" aria-checked={until === "never"} className={until === "never" ? "is-on" : ""} onClick={() => setUntil("never")}>
                  Until I remove it
                </button>
                <button type="button" role="radio" aria-checked={until === "done"} className={until === "done" ? "is-on" : ""} onClick={() => setUntil("done")}>
                  Until the workshop ends
                </button>
              </div>
              {until === "done" && (
                <p className="sis-hint">
                  <Ic n="clock" size={13} /> Adds a time-limited link that stops working when the workshop ends.
                </p>
              )}
              {externals.length > 0 && (
                <label className="sis-opt">
                  <input type="checkbox" checked={guest} onChange={(e) => setGuest(e.target.checked)} />
                  <span>
                    Also add {externals.map((p) => p?.name).join(" and ")} to the Workshop project as a guest
                    <em>Guests are outside Northstar and can comment on project files.</em>
                  </span>
                </label>
              )}
            </>
          )}
        </section>
      </div>

      <footer className="sis-sh-foot">
        <div className={`sis-to${nudge ? " is-nudge" : ""}`} key={nudge}>
          <span className="sis-to-l">To</span>
          {mode === "link" ? (
            <span className="sis-chip-in"><Ic n="globe" size={12} /> Anyone with the link</span>
          ) : people.length + groups.length === 0 ? (
            <span className="sis-to-empty">{nudge ? "Choose at least one person or group" : "Nobody chosen yet"}</span>
          ) : (
            <>
              {people.map((id) => {
                const p = personOf(id);
                return (
                  <button key={id} type="button" className="sis-chip-in" onClick={() => toggle(setPeople, id)} title="Remove">
                    {p?.name} <span className="sis-chip-org">{p ? p.orgLabel.split(" (")[0] : ""}</span> <Ic n="close" size={10} />
                  </button>
                );
              })}
              {groups.map((id) => (
                <button key={id} type="button" className="sis-chip-in" onClick={() => toggle(setGroups, id)} title="Remove">
                  {GROUPS.find((g) => g.id === id)?.name ?? TEAM_ID} <Ic n="close" size={10} />
                </button>
              ))}
            </>
          )}
        </div>
        <div className="sis-sh-act">
          <button type="button" className="sis-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="sis-btn sis-btn--primary" onClick={go} aria-disabled={!ready || busy}>
            {busy ? (<><span className="sis-spin" aria-hidden="true" /> Sharing…</>) : (<><Ic n="share" size={14} /> Share</>)}
          </button>
        </div>
      </footer>
    </div>
  );
}
